import React, { useMemo, useState } from "react";
import { Box, Button, Paper, Typography } from "@mui/material";
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { buildTenantCostTimeline } from "../utils/tenantCostTimeline";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip);

const COLORS = ["#ea580c", "#2563eb", "#0d9488", "#9333ea", "#dc2626", "#a16207", "#0891b2", "#db2777", "#4f46e5", "#4d7c0f"];
const lineColor = (index) => COLORS[index] || `hsl(${(index * 137.508) % 360}, 65%, 42%)`;
const formatCost = (value) => value.toLocaleString(undefined, { maximumFractionDigits: 4 });
const dayLabel = (day) => new Date(`${day}T12:00:00`).toLocaleDateString(undefined, {
  day: "numeric", month: "short", year: "numeric",
});

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  animation: false,
  interaction: { mode: "index", intersect: false },
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: { label: (context) => `${context.dataset.label}: ${formatCost(context.parsed.y)}` },
    },
  },
  scales: {
    x: { grid: { display: false }, ticks: { maxTicksLimit: 10, maxRotation: 0 } },
    y: {
      beginAtZero: true,
      title: { display: true, text: "preezie internal cost USD" },
      grid: { color: "#f1f5f9" },
    },
  },
};

const UsageCostChart = ({ runs, fromDate, toDate, tenantLimit = 10 }) => {
  const [hiddenTenants, setHiddenTenants] = useState(() => new Set());
  const timeline = useMemo(() => buildTenantCostTimeline(runs, fromDate, toDate), [runs, fromDate, toDate]);
  const shownTenants = timeline.tenants.slice(0, tenantLimit);
  const data = {
    labels: timeline.days.map(dayLabel),
    datasets: shownTenants.map((tenant, index) => ({
      tenantId: tenant.id,
      label: `${tenant.name} (${tenant.id || "unknown"})`,
      data: tenant.dailyCosts,
      borderColor: lineColor(index),
      backgroundColor: lineColor(index),
      borderWidth: 2,
      pointRadius: timeline.days.length > 31 ? 0 : 3,
      pointHoverRadius: 5,
      tension: 0,
      hidden: hiddenTenants.has(tenant.id),
    })),
  };
  const toggleTenant = (id) => setHiddenTenants((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });

  return (
    <Paper component="section" aria-labelledby="tenant-cost-chart-title" variant="outlined"
      sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, borderRadius: 3, borderColor: "#e5e7eb", minWidth: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5, mb: 1.5 }}>
        <Box>
          <Typography id="tenant-cost-chart-title" component="h3" variant="subtitle1" fontWeight={700}>Most costly tenants</Typography>
          <Typography variant="caption" color="text.secondary">
            Daily internal costs by recorded date (local time). Ranked by total cost in the filtered range.
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary">Top {tenantLimit} · Set by Rows per page</Typography>
      </Box>
      {shownTenants.length ? <>
        <Box sx={{ height: { xs: 240, sm: 280 }, position: "relative", minWidth: 0 }}>
          <Line data={data} options={chartOptions} datasetIdKey="tenantId" role="img"
            aria-label={`Daily internal cost USD for ${shownTenants.length} tenants`}
            fallbackContent="Daily internal cost in USD per tenant, grouped by run date." />
        </Box>
        <Box aria-label="Chart legend" sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
          {shownTenants.map((tenant, index) => (
            <Button key={tenant.id} size="small" aria-pressed={!hiddenTenants.has(tenant.id)}
              aria-label={`${hiddenTenants.has(tenant.id) ? "Show" : "Hide"} ${tenant.name} (${tenant.id || "unknown"})`}
              title={`${tenant.name} (${tenant.id || "unknown"}) · Total: ${formatCost(tenant.totalCost)}`}
              onClick={() => toggleTenant(tenant.id)}
              sx={{ textTransform: "none", fontWeight: 400, color: "text.secondary", opacity: hiddenTenants.has(tenant.id) ? 0.45 : 1, minWidth: 0, maxWidth: "100%", gap: 0.75 }}>
              <Box component="span" sx={{ width: 16, height: 3, backgroundColor: lineColor(index), flexShrink: 0 }} />
              <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tenant.name} · {formatCost(tenant.totalCost)}</Box>
            </Button>
          ))}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
          Showing {shownTenants.length} of {timeline.tenants.length} tenants with available costs. Click a tenant in the legend to hide or show its line.
        </Typography>
      </> : <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>No cost data available for these filters.</Typography>}
    </Paper>
  );
};

export default UsageCostChart;
